import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowLeft, Save, Plus, X, Sparkles, Heart, ShieldOff, Lightbulb,
         AlertTriangle, Target, Lock, Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TagInput = ({ tags, onChange, placeholder, color = "gold", testid,
                    suggestKind = null, suggestContext = "" }) => {
  const [draft, setDraft] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const colorClasses = {
    gold:   "bg-[#C5A880]/10 border-[#C5A880]/30 text-[#C5A880]",
    green:  "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    red:    "bg-rose-500/10 border-rose-500/30 text-rose-300",
    neutral:"bg-white/5 border-white/10 text-[#DCD7D2]",
  }[color];
  const add = (val) => {
    const v = (val ?? draft).trim();
    if (!v) return;
    if (tags.some(t => t.toLowerCase() === v.toLowerCase())) {
      if (val == null) setDraft("");
      return;
    }
    onChange([...tags, v]);
    if (val == null) setDraft("");
  };

  const fetchSuggestions = async () => {
    if (!suggestKind) return;
    setAiLoading(true);
    setAiOpen(true);
    try {
      const r = await client.post("/profile/extras/suggest-tags", {
        kind: suggestKind,
        description: suggestContext || "",
        existing: tags,
        count: 8,
      });
      setAiSuggestions(r.data?.suggestions || []);
      if ((r.data?.suggestions || []).length === 0) {
        toast.message("A IA não conseguiu gerar tags novas. Tenta dar mais contexto na auto-descrição.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao pedir sugestões à IA.";
      toast.error(msg);
      setAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-3">
        {tags.map((t, i) => (
          <span key={`${t}-${i}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs ${colorClasses}`}>
            {t}
            <button onClick={() => onChange(tags.filter((_, idx) => idx !== i))}
              className="opacity-60 hover:opacity-100" aria-label={`Remover ${t}`}
              data-testid={`${testid}-remove-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 min-w-[180px] bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#C5A880]/50 focus:outline-none"
          data-testid={`${testid}-input`} />
        <button onClick={() => add()} className="btn-secondary text-sm inline-flex items-center gap-1"
          data-testid={`${testid}-add`}>
          <Plus className="w-4 h-4" /> Adicionar
        </button>
        {suggestKind && (
          <button type="button" onClick={fetchSuggestions} disabled={aiLoading}
            className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#C5A880]/40 bg-[#C5A880]/10 text-[#C5A880] hover:bg-[#C5A880]/20 transition disabled:opacity-50"
            data-testid={`${testid}-ai-btn`}
            title="Pede ao Claude tags adaptadas ao teu perfil">
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {aiLoading ? "A pensar…" : "Sugerir com IA"}
          </button>
        )}
      </div>

      {aiOpen && !aiLoading && (
        <div className="mt-3 p-3 bg-[#0C0E12] border border-[#C5A880]/30 rounded-lg" data-testid={`${testid}-ai-panel`}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="text-xs text-[#A29F9B]">
              Sugestões IA (clica para adicionar). Não substituem o teu pensamento — refinam-no.
            </p>
            <button onClick={() => { setAiOpen(false); setAiSuggestions([]); }}
              className="text-[#A29F9B] hover:text-[#F5F2EE]" aria-label="Fechar sugestões"
              data-testid={`${testid}-ai-close`}>
              <X className="w-4 h-4" />
            </button>
          </div>
          {aiSuggestions.length === 0 ? (
            <p className="text-xs text-[#A29F9B] italic">Sem sugestões disponíveis.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {aiSuggestions.map((s, i) => (
                <button key={`${s}-${i}`} onClick={() => {
                    add(s);
                    setAiSuggestions((arr) => arr.filter((x) => x !== s));
                  }}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs hover:opacity-80 transition ${colorClasses}`}
                  data-testid={`${testid}-ai-pick-${i}`}>
                  <Plus className="w-3 h-3" /> {s}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AboutMe = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await client.get("/profile/extras");
        // Assign client-side stable keys to goals so React doesn't reuse the
        // wrong DOM node when items above are deleted.
        const d = r.data || {};
        if (Array.isArray(d.life_goals)) {
          d.life_goals = d.life_goals.map((g, i) => ({
            ...g,
            _key: g._key || `goal-${i}-${Math.random().toString(36).slice(2, 8)}`,
          }));
        }
        setData(d);
      } catch (err) {
        console.warn("[AboutMe] load failed", err);
        toast.error("Falha a carregar.");
      }
      finally { setLoading(false); }
    })();
  }, []);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updateGoal = (idx, patch) => setData((d) => ({
    ...d, life_goals: d.life_goals.map((g, i) => i === idx ? { ...g, ...patch } : g),
  }));
  const removeGoal = (idx) => setData((d) => ({
    ...d, life_goals: d.life_goals.filter((_, i) => i !== idx),
  }));
  const addGoal = () => setData((d) => ({
    ...d,
    life_goals: [
      ...(d.life_goals || []),
      // Stable client-side key so React preserves input focus when items
      // above are removed. Stripped server-side via the payload filter.
      { _key: (typeof crypto !== "undefined" && crypto.randomUUID
               ? crypto.randomUUID()
               : `goal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
        description: "", target_date: "" },
    ],
  }));

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const payload = {
        ...data,
        // Strip client-side `_key` before send; backend doesn't expect it.
        life_goals: (data.life_goals || [])
          .filter(g => (g.description || "").trim())
          .map(({ _key, ...rest }) => rest),
      };
      const r = await client.put("/profile/extras", payload);
      // Re-apply stable keys on the returned payload so editing continues
      // to preserve focus after save.
      const saved = r.data || {};
      if (Array.isArray(saved.life_goals)) {
        saved.life_goals = saved.life_goals.map((g, i) => ({
          ...g, _key: `goal-${i}-${Math.random().toString(36).slice(2, 8)}`,
        }));
      }
      setData(saved);
      toast.success("Sobre mim guardado. Compatibilidade recalculada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao guardar.");
    } finally { setSaving(false); }
  };

  if (loading || !data) return (
    <div className="min-h-screen bg-[#0C0E12]"><Navbar /><div className="p-12 text-[#A29F9B]">A carregar...</div></div>
  );

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="about-me-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/profile" className="text-sm text-[#A29F9B] hover:text-[#C5A880] inline-flex items-center gap-2 mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Voltar ao perfil
        </Link>

        <div className="flex items-center gap-2 mb-3">
          <Lock className="w-3.5 h-3.5 text-[#C5A880]" />
          <span className="label-uppercase">Privado · só tu vês</span>
        </div>
        <h1 className="font-display text-3xl md:text-5xl mb-3">Sobre mim</h1>
        <p className="text-[#A29F9B] mb-10 leading-relaxed">
          Esta zona é apenas tua. Ajuda-te a clarificar quem és e o que procuras —
          e refina silenciosamente os teus matches.
        </p>

        {/* Self description */}
        <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8 mb-6" data-testid="section-self">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#C5A880]" />
            <h2 className="font-display text-xl">Como me vejo</h2>
          </div>
          <p className="text-xs text-[#A29F9B] mb-3">
            Descreve-te com as tuas palavras. As <em>tags</em> que escolheres entram no cálculo
            de compatibilidade de valores quando coincidem com o que outros procuram.
          </p>
          <textarea value={data.self_description} onChange={(e) => setField("self_description", e.target.value)}
            placeholder="Ex.: Vejo-me como uma pessoa curiosa, leal, com gosto pelo silêncio e pela arte..."
            rows={4} maxLength={2000}
            className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-3 text-sm focus:border-[#C5A880]/50 focus:outline-none mb-5"
            data-testid="self-description" />
          <span className="label-uppercase block mb-3">Palavras-chave que me definem</span>
          <TagInput tags={data.self_keywords} onChange={(v) => setField("self_keywords", v)}
            placeholder="Honestidade, curiosidade, calma..." color="gold" testid="self-keywords"
            suggestKind="self_keywords" suggestContext={data.self_description} />
        </section>

        {/* Values */}
        <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8 mb-6" data-testid="section-values">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-4 h-4 text-[#C5A880]" />
            <h2 className="font-display text-xl">Os meus valores</h2>
          </div>

          <div className="space-y-6">
            <div>
              <label className="text-sm text-[#F5F2EE] flex items-center gap-2 mb-2">
                <Heart className="w-3.5 h-3.5 text-emerald-400" /> Atraentes
              </label>
              <p className="text-xs text-[#A29F9B] mb-3">Valores que te atraem em alguém.</p>
              <TagInput tags={data.values_attractive} onChange={(v) => setField("values_attractive", v)}
                placeholder="Lealdade, ambição saudável..." color="green" testid="values-attractive"
                suggestKind="values_attractive" suggestContext={data.self_description} />
            </div>

            <div>
              <label className="text-sm text-[#F5F2EE] flex items-center gap-2 mb-2">
                <ShieldOff className="w-3.5 h-3.5 text-rose-400" /> Repelentes
              </label>
              <p className="text-xs text-[#A29F9B] mb-3">Valores que para ti são deal-breakers.</p>
              <TagInput tags={data.values_repellent} onChange={(v) => setField("values_repellent", v)}
                placeholder="Mentira, arrogância..." color="red" testid="values-repellent"
                suggestKind="values_repellent" suggestContext={data.self_description} />
            </div>

            <div>
              <label className="text-sm text-[#F5F2EE] flex items-center gap-2 mb-2">
                Neutros
              </label>
              <p className="text-xs text-[#A29F9B] mb-3">Indiferentes para ti — nem atraem nem repelem.</p>
              <TagInput tags={data.values_neutral} onChange={(v) => setField("values_neutral", v)}
                placeholder="Por exemplo: religião praticante, viagens..." color="neutral" testid="values-neutral"
                suggestKind="values_neutral" suggestContext={data.self_description} />
            </div>
          </div>
        </section>

        {/* Beliefs */}
        <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8 mb-6" data-testid="section-beliefs">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-4 h-4 text-[#C5A880]" />
            <h2 className="font-display text-xl">As minhas crenças</h2>
          </div>

          <div className="space-y-6">
            <div>
              <label className="text-sm text-[#F5F2EE] flex items-center gap-2 mb-2">
                <Lightbulb className="w-3.5 h-3.5 text-emerald-400" /> Potenciadoras
              </label>
              <p className="text-xs text-[#A29F9B] mb-3">Crenças que te fortalecem.</p>
              <TagInput tags={data.beliefs_empowering} onChange={(v) => setField("beliefs_empowering", v)}
                placeholder="Posso aprender qualquer coisa, mereço amor..." color="green" testid="beliefs-empowering"
                suggestKind="beliefs_empowering" suggestContext={data.self_description} />
            </div>

            <div>
              <label className="text-sm text-[#F5F2EE] flex items-center gap-2 mb-2">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-400" /> Limitadoras
              </label>
              <p className="text-xs text-[#A29F9B] mb-3">Crenças que reconheces que te limitam.</p>
              <TagInput tags={data.beliefs_limiting} onChange={(v) => setField("beliefs_limiting", v)}
                placeholder="Sou tímido demais, não tenho jeito para..." color="red" testid="beliefs-limiting"
                suggestKind="beliefs_limiting" suggestContext={data.self_description} />
            </div>
          </div>
        </section>

        {/* Life goals */}
        <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8 mb-10" data-testid="section-goals">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-[#C5A880]" />
            <h2 className="font-display text-xl">Objetivos de vida</h2>
          </div>
          <p className="text-xs text-[#A29F9B] mb-5">
            Marca os teus objetivos com data — ajuda-te a manter direção e clareza.
          </p>

          <div className="space-y-3">
            {(data.life_goals || []).map((g, i) => (
              <div key={g._key || `goal-${i}`} className="flex flex-col sm:flex-row gap-3 items-start sm:items-center bg-[#0C0E12] border border-white/5 rounded-xl p-3" data-testid={`goal-${i}`}>
                <input value={g.description} onChange={(e) => updateGoal(i, { description: e.target.value })}
                  placeholder="Descrição do objetivo"
                  className="flex-1 w-full bg-transparent text-sm focus:outline-none"
                  data-testid={`goal-desc-${i}`} />
                <input type="date" value={g.target_date || ""} onChange={(e) => updateGoal(i, { target_date: e.target.value })}
                  className="bg-[#161920] border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:border-[#C5A880]/50 focus:outline-none"
                  data-testid={`goal-date-${i}`} />
                <button onClick={() => removeGoal(i)} className="text-[#A29F9B] hover:text-rose-400"
                  aria-label="Remover objetivo" data-testid={`goal-remove-${i}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <button onClick={addGoal} className="btn-secondary text-sm inline-flex items-center gap-1 mt-4" data-testid="goal-add">
            <Plus className="w-4 h-4" /> Novo objetivo
          </button>
        </section>

        <div className="sticky bottom-4 flex justify-end">
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 shadow-2xl shadow-black/40"
            data-testid="save-extras">
            <Save className="w-4 h-4" /> {saving ? "A guardar..." : "Guardar e recalcular"}
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutMe;
